import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/lib/audit";
import {
  COUNTRY_EXPERIENCE_REGISTRY,
  COUNTRY_HOME_PATH,
  SUPPORTED_COUNTRIES,
  type CountryCode,
} from "@/lib/country/countryExperienceEngine";
import {
  computeInvestmentRecommendation,
  getAllCountryMetrics,
  getInfluencerRanking,
  getViralitySummary,
  subscribe as subscribeMarket,
  type CountryMetrics,
} from "@/lib/marketDomination/marketScoreEngine";
import { INFLUENCERS } from "@/lib/growth/influencerRegistry";
import { GovernanceTab } from "@/components/admin/governance/GovernanceTab";
import {
  SURFACE_KEYS,
  SURFACE_LABELS,
  activateFullBoost,
  deactivateBoost,
  readOverrides,
  resetOverrides,
  setContent,
  setSurface,
  subscribeOverrides,
  toggleEmergency,
  toggleInfluencerPin,
  toggleInfluencerVisibility,
  type CountryContent,
  type SurfaceKey,
  type WorldOverridesStore,
} from "@/lib/worldDomination/overrides";

export const Route = createFileRoute("/admin/world-domination")({
  head: () => ({
    meta: [
      { title: "🌎 World Domination Center · VerseSport SuperAdmin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: WorldDominationPage,
});

function statusOf(score: number): "green" | "yellow" | "red" {
  if (score >= 60) return "green";
  if (score >= 30) return "yellow";
  return "red";
}
function statusEmoji(s: "green" | "yellow" | "red") {
  return s === "green" ? "🟢" : s === "yellow" ? "🟡" : "🔴";
}
function statusLabel(s: "green" | "yellow" | "red") {
  return s === "green" ? "Dominando" : s === "yellow" ? "Creciendo" : "Atención";
}

function WorldDominationPage() {
  const [tick, setTick] = useState(0);
  const [previewCountry, setPreviewCountry] = useState<CountryCode | null>(null);
  const [openCountry, setOpenCountry] = useState<CountryCode | null>(null);
  const [view, setView] = useState<"ops" | "governance">("ops");

  useEffect(() => {
    void trackEvent("world_domination_center_opened", {});
    const u1 = subscribeMarket(() => setTick((t) => t + 1));
    const u2 = subscribeOverrides(() => setTick((t) => t + 1));
    return () => { u1(); u2(); };
  }, []);

  const metrics = useMemo(() => getAllCountryMetrics(), [tick]);
  const overrides = useMemo<WorldOverridesStore>(() => readOverrides(), [tick]);
  const invest = useMemo(() => computeInvestmentRecommendation(10_000), [tick]);
  const virality = useMemo(() => getViralitySummary(), [tick]);

  const globals = useMemo(() => {
    return metrics.reduce(
      (acc, m) => ({
        users: acc.users + m.registered7d + m.active7d,
        shares: acc.shares + m.shares7d,
        referrals: acc.referrals + m.referrals7d,
        predictions: acc.predictions + m.predictions7d,
        retention: acc.retention + m.retention.d7,
        score: acc.score + m.score,
      }),
      { users: 0, shares: 0, referrals: 0, predictions: 0, retention: 0, score: 0 },
    );
  }, [metrics]);

  const avgRetention = metrics.length ? Math.round(globals.retention / metrics.length) : 0;
  const avgScore = metrics.length ? Math.round(globals.score / metrics.length) : 0;
  const globalStatus = statusOf(avgScore);

  return (
    <main className="mx-auto max-w-7xl space-y-4 px-3 py-4">
      {/* HEADER */}
      <header className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="text-2xl font-black tracking-tight">🌎 VerseSport World Domination Center</h1>
            <p className="text-[12px] text-muted-foreground">
              Torre de control multinacional · controla, simula y administra cada país desde un solo panel.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-black">
            <span className="rounded-full bg-background/60 px-2 py-1">SuperAdmin</span>
            <span
              className={`rounded-full px-2 py-1 ${globalStatus === "green" ? "bg-emerald-500/20 text-emerald-400" : globalStatus === "yellow" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`}
            >
              {statusEmoji(globalStatus)} Global · {statusLabel(globalStatus)}
            </span>
          </div>
        </div>
        <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <GlobalKpi label="Usuarios" value={globals.users} />
          <GlobalKpi label="Shares" value={globals.shares} />
          <GlobalKpi label="Referrals" value={globals.referrals} />
          <GlobalKpi label="Predicciones" value={globals.predictions} />
          <GlobalKpi label="Retención avg" value={`${avgRetention}%`} />
          <GlobalKpi label="Score global" value={`${avgScore}/100`} />
        </ul>
        <nav className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
          <AdminPill to="/admin/market-domination" label="Market War Room" />
          <AdminPill to="/admin/brasil-domination" label="🇧🇷 Brasil" />
          <AdminPill to="/admin/india-domination" label="🇮🇳 India" />
          <AdminPill to="/admin/country-growth" label="Country Growth" />
          <AdminPill to="/admin" label="Back to Admin" />
        </nav>
        <nav className="mt-3 flex gap-1.5 text-[11px] font-black" role="tablist" aria-label="World Domination views">
          <button
            role="tab"
            aria-selected={view === "ops"}
            onClick={() => setView("ops")}
            className={`rounded-full px-3 py-1 ${view === "ops" ? "bg-primary text-primary-foreground" : "border border-border bg-background/60"}`}
          >
            🛰️ Operations
          </button>
          <button
            role="tab"
            aria-selected={view === "governance"}
            onClick={() => setView("governance")}
            className={`rounded-full px-3 py-1 ${view === "governance" ? "bg-primary text-primary-foreground" : "border border-border bg-background/60"}`}
          >
            🧠 Governance
          </button>
        </nav>
      </header>

      {view === "governance" ? (
        <GovernanceTab />
      ) : (
        <>
          {/* EXECUTIVE MODE — qué está pasando ahora mismo */}
          <ExecutiveMode metrics={metrics} invest={invest} virality={virality} />

      {/* COUNTRY COMMAND GRID */}
      <section aria-label="Country Command Grid">
        <h2 className="mb-2 text-sm font-black">🛰️ Country Command Grid</h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {metrics.map((m) => (
            <CountryCard
              key={m.country}
              m={m}
              overrides={overrides[m.country]}
              onPreview={() => setPreviewCountry(m.country)}
              onOpen={() => setOpenCountry(m.country)}
            />
          ))}
        </div>
      </section>

      {/* WORLD MAP */}
      <WorldMarketMap metrics={metrics} onPick={(c) => setOpenCountry(c)} />

      {/* MARKET ATTACK CENTER */}
      <MarketAttackCenter invest={invest} metrics={metrics} />

      {/* INFLUENCER ARMY */}
      <InfluencerArmyCenter overrides={overrides} />

      {/* LIVE OPS — boost / emergency / content per country */}
      <LiveOpsCenter overrides={overrides} />
        </>
      )}

      {/* Modals */}
      {previewCountry && (
        <CountryPreviewFrame
          country={previewCountry}
          overrides={overrides[previewCountry]}
          onClose={() => setPreviewCountry(null)}
        />
      )}
      {openCountry && (
        <CountryControlCenter
          country={openCountry}
          metrics={metrics.find((m) => m.country === openCountry)!}
          overrides={overrides[openCountry]}
          onPreview={() => { setPreviewCountry(openCountry); }}
          onClose={() => setOpenCountry(null)}
        />
      )}

      <footer className="pt-3 text-center text-[10px] text-muted-foreground">
        Overrides locales (localStorage). Ninguna acción modifica producción.
        <button
          onClick={() => { if (confirm("¿Resetear todos los overrides?")) resetOverrides(); }}
          className="ml-2 underline"
        >
          Reset overrides
        </button>
      </footer>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Reusable bits                                                       */
/* ------------------------------------------------------------------ */

function GlobalKpi({ label, value }: { label: string; value: number | string }) {
  const v = typeof value === "number" ? value.toLocaleString("en-US") : value;
  return (
    <li className="rounded-xl border border-border bg-background/60 px-3 py-2">
      <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-black tabular-nums">{v}</div>
    </li>
  );
}

function AdminPill({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to as never}
      className="rounded-full border border-border bg-background/60 px-2.5 py-1 font-black hover:bg-accent"
    >
      {label}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Executive Mode                                                      */
/* ------------------------------------------------------------------ */

function ExecutiveMode({
  metrics,
  invest,
  virality,
}: {
  metrics: CountryMetrics[];
  invest: ReturnType<typeof computeInvestmentRecommendation>;
  virality: ReturnType<typeof getViralitySummary>;
}) {
  const leader = metrics[0];
  const fastest = [...metrics].sort((a, b) => b.growth.d24 - a.growth.d24)[0];
  const viral = virality[0];
  const bestRet = [...metrics].sort((a, b) => b.retention.d7 - a.retention.d7)[0];
  const worst = metrics[metrics.length - 1];
  const allInfluencers = getInfluencerRanking(null, 1);
  const topInfluencer = allInfluencers[0];

  return (
    <section
      aria-label="Executive Mode"
      className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-rose-500/10 p-4"
    >
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-black">⚡ Executive Mode · ¿Qué está pasando ahora?</h2>
        <span className="text-[10px] text-muted-foreground">7d rolling</span>
      </header>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        <ExecCell label="País líder" value={leader ? `${flag(leader.country)} ${leader.country}` : "—"} hint={leader ? `${leader.score}/100` : ""} />
        <ExecCell label="Mayor crecimiento" value={fastest ? `${flag(fastest.country)} ${fastest.country}` : "—"} hint={fastest ? `${fastest.growth.d24 > 0 ? "+" : ""}${fastest.growth.d24}% 24h` : ""} />
        <ExecCell label="Más viral" value={viral ? `${flag(viral.country)} ${viral.country}` : "—"} hint={viral ? `k=${viral.kFactor}` : ""} />
        <ExecCell label="Mejor retención" value={bestRet ? `${flag(bestRet.country)} ${bestRet.country}` : "—"} hint={bestRet ? `${bestRet.retention.d7}% d7` : ""} />
        <ExecCell label="Peor mercado" value={worst ? `${flag(worst.country)} ${worst.country}` : "—"} hint={worst ? `${worst.score}/100` : ""} />
        <ExecCell
          label="Influencer líder"
          value={topInfluencer ? `${flag(topInfluencer.country)} ${topInfluencer.displayName}` : "—"}
          hint={topInfluencer ? `${topInfluencer.signups}↑ ${topInfluencer.referrals}🔗` : ""}
        />
        <ExecCell label="Inversión recomendada" value={`${flag(invest.top)} ${invest.top}`} hint={`USD 10,000`} />
        <ExecCell
          label="Acción recomendada"
          value={recommendedAction(metrics, invest)}
          hint=""
        />
      </ul>
    </section>
  );
}

function ExecCell({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <li className="rounded-xl border border-border bg-background/60 px-3 py-2">
      <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-black">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </li>
  );
}

function recommendedAction(metrics: CountryMetrics[], invest: ReturnType<typeof computeInvestmentRecommendation>): string {
  const worst = metrics[metrics.length - 1];
  if (!worst) return "Mantén el rumbo";
  if (worst.score < 20) return `Activar Boost en ${worst.country}`;
  return `Doblar inversión en ${invest.top}`;
}

function flag(c: CountryCode): string {
  return COUNTRY_EXPERIENCE_REGISTRY[c]?.flag ?? "🌐";
}

/* ------------------------------------------------------------------ */
/* Country Card                                                        */
/* ------------------------------------------------------------------ */

function CountryCard({
  m,
  overrides,
  onPreview,
  onOpen,
}: {
  m: CountryMetrics;
  overrides: WorldOverridesStore[CountryCode];
  onPreview: () => void;
  onOpen: () => void;
}) {
  const cfg = COUNTRY_EXPERIENCE_REGISTRY[m.country];
  const s = statusOf(m.score);
  const growthScore = Math.min(100, Math.max(0, 50 + m.growth.d24));
  const shareScore = Math.min(100, (m.shares7d / 3));
  const influencerScore = Math.min(100, (m.referrals7d * 2 + m.shares7d) / 5);
  const conversionScore = m.registered7d ? Math.min(100, Math.round((m.predictions7d / m.registered7d) * 100)) : 0;
  const boosted = overrides.boost.active;
  const offline = overrides.emergencyOff;
  return (
    <article
      className="rounded-2xl border border-border bg-background/60 p-3"
      style={{ borderColor: cfg.accent_colors.primary }}
    >
      <header className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{cfg.flag}</span>
          <div>
            <div className="text-sm font-black">{cfg.country_name}</div>
            <div className="text-[10px] text-muted-foreground">{cfg.language}</div>
          </div>
        </div>
        <span className="text-[10px] font-black">{statusEmoji(s)} {statusLabel(s)}</span>
      </header>
      {(boosted || offline) && (
        <div className={`mb-2 rounded-md px-2 py-1 text-[10px] font-black ${offline ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>
          {offline ? "⛔ Emergencia · superficies apagadas" : "🚀 Boost activo"}
        </div>
      )}
      <div className="mb-2 grid grid-cols-3 gap-1.5 text-center text-[10px]">
        <ScoreCell label="Dom" value={m.score} />
        <ScoreCell label="Grow" value={Math.round(growthScore)} />
        <ScoreCell label="Ret" value={m.retention.d7} suffix="%" />
        <ScoreCell label="Share" value={Math.round(shareScore)} />
        <ScoreCell label="Infl" value={Math.round(influencerScore)} />
        <ScoreCell label="Conv" value={conversionScore} suffix="%" />
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-[11px] font-black">
        <button
          onClick={onOpen}
          className="rounded-md bg-primary px-2 py-1.5 text-primary-foreground hover:opacity-90"
        >
          Control Center
        </button>
        <button
          onClick={onPreview}
          className="rounded-md border border-border bg-background/60 px-2 py-1.5 hover:bg-accent"
        >
          Preview
        </button>
      </div>
    </article>
  );
}

function ScoreCell({ label, value, suffix }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="rounded-md border border-border/50 bg-background/40 py-1">
      <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-black tabular-nums">{value}{suffix ?? ""}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* World Map (lightweight SVG)                                         */
/* ------------------------------------------------------------------ */

function WorldMarketMap({ metrics, onPick }: { metrics: CountryMetrics[]; onPick: (c: CountryCode) => void }) {
  // Approximate world coords (lightweight; not geographic accuracy).
  const COORDS: Record<CountryCode, { x: number; y: number }> = {
    BR: { x: 320, y: 220 },
    MX: { x: 200, y: 145 },
    AR: { x: 305, y: 290 },
    IN: { x: 580, y: 175 },
  };
  return (
    <section aria-label="Global Map" className="rounded-2xl border border-border bg-background/60 p-3">
      <h2 className="mb-2 text-sm font-black">🗺️ Global Market Map</h2>
      <div className="overflow-x-auto">
        <svg viewBox="0 0 800 360" className="mx-auto w-full max-w-3xl" role="img" aria-label="World market status">
          <rect width="800" height="360" rx="14" fill="oklch(0.18 0.02 240)" />
          {/* abstract continents */}
          {ABSTRACT_LANDS.map((d, i) => (
            <path key={i} d={d} fill="oklch(0.28 0.03 240)" />
          ))}
          {metrics.map((m) => {
            const c = COORDS[m.country];
            const s = statusOf(m.score);
            const color = s === "green" ? "oklch(0.7 0.16 145)" : s === "yellow" ? "oklch(0.8 0.16 90)" : "oklch(0.65 0.2 25)";
            return (
              <g
                key={m.country}
                onClick={() => onPick(m.country)}
                style={{ cursor: "pointer" }}
              >
                <circle cx={c.x} cy={c.y} r={22} fill={color} opacity={0.25} />
                <circle cx={c.x} cy={c.y} r={12} fill={color} />
                <text x={c.x} y={c.y + 4} textAnchor="middle" fontSize="12" fontWeight="900" fill="#fff">
                  {m.country}
                </text>
                <text x={c.x} y={c.y + 36} textAnchor="middle" fontSize="10" fill="#fff">
                  {m.score}/100
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
}

const ABSTRACT_LANDS = [
  // very abstract land masses, purely decorative
  "M50,120 Q130,80 220,130 Q260,160 230,210 Q190,240 140,225 Q70,205 50,170 Z",
  "M260,180 Q330,140 360,210 Q380,280 320,320 Q260,310 250,250 Z",
  "M400,90 Q470,60 540,110 Q570,150 520,180 Q450,170 400,140 Z",
  "M520,140 Q620,120 700,160 Q740,200 680,240 Q600,250 540,210 Z",
];

/* ------------------------------------------------------------------ */
/* Market Attack Center                                                */
/* ------------------------------------------------------------------ */

function MarketAttackCenter({
  invest,
  metrics,
}: {
  invest: ReturnType<typeof computeInvestmentRecommendation>;
  metrics: CountryMetrics[];
}) {
  return (
    <section aria-label="Market Attack Center" className="rounded-2xl border border-border bg-background/60 p-4">
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-black">🎯 Market Attack Center</h2>
        <span className="text-[10px] text-muted-foreground">Si hoy tuvieras USD 10,000…</span>
      </header>
      <div className="rounded-xl border border-primary/40 bg-primary/10 p-3 text-[12px] font-black">
        {invest.copy}
      </div>
      <ol className="mt-3 grid gap-1.5">
        {invest.ranking.map((r, idx) => {
          const m = metrics.find((x) => x.country === r.country);
          const s = statusOf(m?.score ?? 0);
          return (
            <li
              key={r.country}
              className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2 text-[12px]"
            >
              <span className="flex items-center gap-2">
                <span className="w-5 text-center font-black">{idx + 1}</span>
                <span>{flag(r.country)} {COUNTRY_EXPERIENCE_REGISTRY[r.country].country_name}</span>
              </span>
              <span className="flex items-center gap-3">
                <span className="text-[10px] text-muted-foreground">Opp {r.investmentScore}/100</span>
                <span className="font-black">{statusEmoji(s)}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Influencer Army Center                                              */
/* ------------------------------------------------------------------ */

function InfluencerArmyCenter({ overrides }: { overrides: WorldOverridesStore }) {
  const ranking = useMemo(() => getInfluencerRanking(null, 200), []);
  const [filter, setFilter] = useState<"ALL" | CountryCode>("ALL");

  const all = Object.values(INFLUENCERS).map((p) => {
    const r = ranking.find((x) => x.slug === p.slug);
    return {
      slug: p.slug,
      country: p.country,
      displayName: p.displayName,
      signups: r?.signups ?? 0,
      shares: r?.shares ?? 0,
      referrals: r?.referrals ?? 0,
      predictions: r?.predictions ?? 0,
      views: r?.views ?? 0,
    };
  });

  const rows = all
    .filter((p) => filter === "ALL" || p.country === filter)
    .map((p) => {
      const hidden = overrides[p.country].hiddenInfluencers.includes(p.slug);
      const pinned = overrides[p.country].pinnedInfluencers.includes(p.slug);
      const score = Math.min(100, p.signups * 4 + p.referrals * 3 + p.shares + p.predictions * 2);
      const retention = Math.min(100, p.predictions * 2);
      return { ...p, hidden, pinned, score, retention };
    })
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || b.score - a.score);

  return (
    <section aria-label="Influencer Army Center" className="rounded-2xl border border-border bg-background/60 p-4">
      <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-black">🪖 Influencer Army Center</h2>
        <div className="flex gap-1 text-[11px] font-black">
          {(["ALL", ...SUPPORTED_COUNTRIES] as const).map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`rounded-full px-2 py-0.5 ${filter === c ? "bg-primary text-primary-foreground" : "border border-border bg-background/40"}`}
            >
              {c === "ALL" ? "ALL" : `${flag(c as CountryCode)} ${c}`}
            </button>
          ))}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead className="text-[9px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-2 py-1">Nombre</th>
              <th className="px-2 py-1">País</th>
              <th className="px-2 py-1 text-right">Users</th>
              <th className="px-2 py-1 text-right">Shares</th>
              <th className="px-2 py-1 text-right">Refs</th>
              <th className="px-2 py-1 text-right">Preds</th>
              <th className="px-2 py-1 text-right">Ret</th>
              <th className="px-2 py-1 text-right">Score</th>
              <th className="px-2 py-1 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.slug}
                className={`border-t border-border/40 ${r.hidden ? "opacity-50" : ""}`}
              >
                <td className="px-2 py-1.5 font-black">
                  {r.pinned && <span className="mr-1">📌</span>}
                  {r.displayName}
                </td>
                <td className="px-2 py-1.5">{flag(r.country)} {r.country}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.signups}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.shares}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.referrals}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.predictions}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.retention}%</td>
                <td className="px-2 py-1.5 text-right font-black tabular-nums">{r.score}</td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    onClick={() => toggleInfluencerPin(r.country, r.slug, !r.pinned)}
                    className="mr-1 rounded border border-border bg-background/40 px-1.5 py-0.5 text-[10px]"
                    title={r.pinned ? "Despriorizar" : "Priorizar"}
                  >
                    {r.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    onClick={() => toggleInfluencerVisibility(r.country, r.slug, !r.hidden)}
                    className="rounded border border-border bg-background/40 px-1.5 py-0.5 text-[10px]"
                    title={r.hidden ? "Mostrar" : "Ocultar"}
                  >
                    {r.hidden ? "Show" : "Hide"}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-3 text-center text-muted-foreground">Sin influencers en este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Live Ops Center (boost + emergency + content + overrides)           */
/* ------------------------------------------------------------------ */

function LiveOpsCenter({ overrides }: { overrides: WorldOverridesStore }) {
  return (
    <section aria-label="Live Operations Center" className="rounded-2xl border border-border bg-background/60 p-4">
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-black">🛠️ Live Operations Center</h2>
        <span className="text-[10px] text-muted-foreground">Reversible · localStorage</span>
      </header>
      <div className="grid gap-3 md:grid-cols-2">
        {SUPPORTED_COUNTRIES.map((c) => (
          <CountryOpsPanel key={c} country={c} state={overrides[c]} />
        ))}
      </div>
    </section>
  );
}

function CountryOpsPanel({
  country,
  state,
}: {
  country: CountryCode;
  state: WorldOverridesStore[CountryCode];
}) {
  const cfg = COUNTRY_EXPERIENCE_REGISTRY[country];
  return (
    <article className="rounded-xl border border-border bg-background/40 p-3">
      <header className="mb-2 flex items-center justify-between">
        <div className="text-[12px] font-black">{cfg.flag} {cfg.country_name}</div>
        <div className="flex gap-1 text-[10px] font-black">
          {state.boost.active ? (
            <button
              onClick={() => { deactivateBoost(country); void trackEvent("world_boost_deactivated", { country }); }}
              className="rounded bg-amber-500/20 px-2 py-0.5 text-amber-300"
            >
              Stop Boost
            </button>
          ) : (
            <button
              onClick={() => { activateFullBoost(country); void trackEvent("world_boost_activated", { country }); }}
              className="rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-300"
            >
              🚀 Boost {country}
            </button>
          )}
          <button
            onClick={() => { toggleEmergency(country, !state.emergencyOff); void trackEvent("world_emergency_toggled", { country, off: !state.emergencyOff }); }}
            className={`rounded px-2 py-0.5 ${state.emergencyOff ? "bg-rose-500/20 text-rose-300" : "border border-border bg-background/60"}`}
          >
            {state.emergencyOff ? "⛔ Off" : "Emergency"}
          </button>
        </div>
      </header>

      {/* Surfaces */}
      <div className="mb-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Overrides de superficies</div>
        <ul className="grid grid-cols-2 gap-1">
          {SURFACE_KEYS.map((k) => (
            <li key={k}>
              <label className="flex items-center justify-between gap-2 rounded border border-border/60 bg-background/60 px-2 py-1 text-[11px]">
                <span>{SURFACE_LABELS[k]}</span>
                <input
                  type="checkbox"
                  checked={state.surfaces[k] && !state.emergencyOff}
                  disabled={state.emergencyOff}
                  onChange={(e) => setSurface(country, k as SurfaceKey, e.target.checked)}
                />
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Content War Room (collapsed inline) */}
      <ContentEditor country={country} content={state.content} />
    </article>
  );
}

function ContentEditor({ country, content }: { country: CountryCode; content: CountryContent }) {
  const [local, setLocal] = useState<CountryContent>(content);
  useEffect(() => setLocal(content), [content]);
  const change = (k: keyof CountryContent, v: string) => setLocal((p) => ({ ...p, [k]: v }));
  const save = () => { setContent(country, local); void trackEvent("world_content_saved", { country }); };
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Content War Room</div>
      <div className="grid gap-1.5">
        {(["headline", "campaign", "banner", "clip"] as Array<keyof CountryContent>).map((k) => (
          <label key={k} className="text-[10px] font-bold">
            <span className="text-muted-foreground">{k}</span>
            <input
              value={local[k]}
              onChange={(e) => change(k, e.target.value)}
              className="mt-0.5 w-full rounded border border-border bg-background/60 px-2 py-1 text-[11px] font-normal"
            />
          </label>
        ))}
        <button
          onClick={save}
          className="self-end rounded bg-primary px-2 py-1 text-[11px] font-black text-primary-foreground"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Country Preview Frame (digital twin)                                */
/* ------------------------------------------------------------------ */

function CountryPreviewFrame({
  country,
  overrides,
  onClose,
}: {
  country: CountryCode;
  overrides: WorldOverridesStore[CountryCode];
  onClose: () => void;
}) {
  const cfg = COUNTRY_EXPERIENCE_REGISTRY[country];
  const url = COUNTRY_HOME_PATH[country] + "?vsPreview=1";
  return (
    <div role="dialog" aria-modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
      <div className="w-full max-w-5xl overflow-hidden rounded-2xl border border-border bg-background">
        <header
          className="flex items-center justify-between p-3"
          style={{ background: cfg.accent_colors.primary, color: "#fff" }}
        >
          <div className="text-sm font-black">{cfg.flag} Preview · {cfg.country_name}</div>
          <div className="flex gap-2 text-[10px] font-black">
            <a href={url} target="_blank" rel="noreferrer" className="rounded bg-black/30 px-2 py-1">Abrir en nueva pestaña</a>
            <button onClick={onClose} className="rounded bg-black/40 px-2 py-1">Cerrar</button>
          </div>
        </header>
        <div className="grid gap-0 md:grid-cols-[260px,1fr]">
          <aside className="border-r border-border p-3 text-[11px]">
            <div className="mb-2 font-black">Branding</div>
            <div className="mb-2 flex gap-1">
              {Object.entries(cfg.accent_colors).map(([k, v]) => (
                <span key={k} className="h-5 w-5 rounded" style={{ background: v as string }} title={k} />
              ))}
            </div>
            <div className="mb-2 font-black">Idioma</div>
            <div className="mb-2 text-muted-foreground">{cfg.language}</div>
            <div className="mb-2 font-black">Hero</div>
            <div className="mb-2">{overrides.content.headline}</div>
            <div className="mb-2 font-black">Campaign</div>
            <div className="mb-2">{overrides.content.campaign}</div>
            <div className="mb-2 font-black">Influencers</div>
            <ul className="space-y-1">
              {cfg.influencers.map((i) => (
                <li key={i.slug}>{i.emoji} {i.displayName}</li>
              ))}
            </ul>
            <div className="mt-2 font-black">CTAs locales</div>
            <ul className="space-y-1">
              {cfg.local_ctas.map((c) => <li key={c.id}>· {c.label}</li>)}
            </ul>
            <div className="mt-3 text-[10px] text-muted-foreground">{cfg.disclaimer}</div>
          </aside>
          <iframe
            title={`Preview ${country}`}
            src={url}
            className="h-[70vh] w-full bg-background"
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Country Control Center (modal)                                      */
/* ------------------------------------------------------------------ */

function CountryControlCenter({
  country,
  metrics,
  overrides,
  onPreview,
  onClose,
}: {
  country: CountryCode;
  metrics: CountryMetrics;
  overrides: WorldOverridesStore[CountryCode];
  onPreview: () => void;
  onClose: () => void;
}) {
  const cfg = COUNTRY_EXPERIENCE_REGISTRY[country];
  return (
    <div role="dialog" aria-modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
      <div className="w-full max-w-3xl space-y-3 overflow-y-auto rounded-2xl border border-border bg-background p-4" style={{ maxHeight: "90vh" }}>
        <header className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black">{cfg.flag} {cfg.country_name} Control Center</h3>
            <p className="text-[11px] text-muted-foreground">Domination {metrics.score}/100 · 7d</p>
          </div>
          <div className="flex gap-1 text-[11px] font-black">
            <button onClick={onPreview} className="rounded border border-border px-2 py-1">Preview</button>
            <button onClick={onClose} className="rounded bg-primary px-2 py-1 text-primary-foreground">Cerrar</button>
          </div>
        </header>
        <CountryOpsPanel country={country} state={overrides} />
        <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
          <KpiMini label="Registros" v={metrics.registered7d} />
          <KpiMini label="Activos" v={metrics.active7d} />
          <KpiMini label="Preds" v={metrics.predictions7d} />
          <KpiMini label="Shares" v={metrics.shares7d} />
          <KpiMini label="Refs" v={metrics.referrals7d} />
          <KpiMini label="WhatsApp" v={metrics.whatsapp7d} />
          <KpiMini label="Telegram" v={metrics.telegram7d} />
          <KpiMini label="d7 Ret" v={`${metrics.retention.d7}%`} />
        </div>
      </div>
    </div>
  );
}

function KpiMini({ label, v }: { label: string; v: number | string }) {
  const val = typeof v === "number" ? v.toLocaleString("en-US") : v;
  return (
    <div className="rounded-md border border-border bg-background/60 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-black tabular-nums">{val}</div>
    </div>
  );
}
